import { ISchool, CreateSchoolInput, SchoolResponse } from '@/src/models/core/school';
import { IUser } from '@/src/models/core/user';
import { IStudent } from '@/src/models/core/student';

// Generate demo school data
export const generateDemoSchools = (adminUsers: IUser[], demoStudents: IStudent[]): ISchool[] => {
  const currentDate = new Date();
  const oneYearFromNow = new Date(currentDate.getTime() + 365 * 24 * 60 * 60 * 1000);
  
  return [
    {
      // Basic Information
      _id: 'school_1',
      name: 'Prestige High School',
      code: 'PHS001',
      email: 'admin@prestigehigh.edu.ng',
      phone: '+2348012345678',
      website: 'https://prestigehigh.edu.ng',
      status: 'active',
      tier: 'premium',
      type: 'private',

      // Location
      address: {
        street: '123 Education Avenue',
        city: 'Ikeja',
        state: 'Lagos',
        country: 'Nigeria',
        postalCode: '100001',
        coordinates: {
          latitude: 6.5244,
          longitude: 3.3792
        }
      },

      // Administrative
      principalName: 'Dr. Adebayo Johnson',
      principalEmail: 'principal@prestigehigh.edu.ng',
      principalPhone: '+2348023456789',
      foundedYear: 1995,
      motto: 'Knowledge, Character, Excellence',
      mission: 'To provide quality education that develops students academically and morally',
      vision: 'To be a leading institution in educational innovation and student development',

      // Academic Structure
      classLevels: ['JSS1', 'JSS2', 'JSS3', 'SS1', 'SS2', 'SS3'],
      subjectsOffered: [
        'Mathematics', 'English Language', 'Physics', 'Chemistry', 'Biology',
        'Geography', 'History', 'Economics', 'Commerce', 'Accounting',
        'Literature in English', 'French', 'Fine Arts', 'Music', 'Physical Education',
        'Computer Studies', 'Agricultural Science', 'Home Economics'
      ],
      academicSession: '2024/2025',
      currentTerm: 1,

      // Statistics
      statistics: {
        totalStudents: 45,
        totalTeachers: 12,
        totalStaff: 8,
        maleStudents: 23,
        femaleStudents: 22,
        classGroups: 18,
        lastUpdated: currentDate
      },

      // User References
      adminId: adminUsers.find(u => u.email === 'admin@prestigehigh.edu.ng')?._id || 'admin_1',
      teacherIds: [
        'teacher_1', 'teacher_2', 'teacher_3', 'teacher_4', 'teacher_5',
        'teacher_6', 'teacher_7', 'teacher_8', 'teacher_9', 'teacher_10'
      ],
      studentIds: demoStudents.slice(0, 45).map(student => student._id),

      // Facilities
      facilities: [
        'Library', 'Science Laboratories', 'Computer Lab', 'Sports Field',
        'Art Studio', 'Music Room', 'Auditorium', 'Cafeteria',
        'Guidance Counseling Office', 'Medical Clinic'
      ],

      // Features
      features: {
        careerGuidance: true,
        onlineAssessments: true,
        parentPortal: true,
        progressTracking: true,
        analytics: true
      },

      // Subscription
      subscription: {
        plan: 'premium',
        startDate: new Date('2024-01-01'),
        endDate: oneYearFromNow,
        isActive: true,
        paymentMethod: 'card',
        billingEmail: 'billing@prestigehigh.edu.ng'
      },

      // Branding
      branding: {
        logo: '/school-logos/prestige-high.png',
        primaryColor: '#1E40AF', // blue-800
        secondaryColor: '#DC2626', // red-600
        favicon: '/favicons/prestige-high.ico'
      },

      // Settings
      settings: {
        language: 'en',
        timezone: 'Africa/Lagos',
        dateFormat: 'DD/MM/YYYY',
        academicCalendar: {
          term1Start: new Date('2024-09-09'),
          term1End: new Date('2024-12-13'),
          term2Start: new Date('2025-01-06'),
          term3Start: new Date('2025-04-14'),
          term3End: new Date('2025-07-18')
        },
        assessmentSettings: {
          autoGrade: true,
          showResults: true,
          retakeAllowed: true
        }
      },

      // Notifications
      notifications: {
        email: {
          studentProgress: true,
          assessmentResults: true,
          announcements: true,
          billing: true
        },
        sms: {
          urgentAlerts: true,
          attendance: true
        },
        push: {
          general: true,
          deadlines: true
        }
      },

      // Integrations
      integrations: {
        lmsEnabled: true,
        lmsUrl: 'https://lms.prestigehigh.edu.ng',
        paymentEnabled: true,
        smsEnabled: true,
        apiKeys: {
          paystack: 'pk_live_prestige_high_key',
          twilio: 'AC_prestige_high_twilio_key'
        }
      },

      // Performance
      performance: {
        averageStudentPerformance: 78,
        studentEngagement: 85,
        assessmentCompletion: 92,
        teacherActivity: 88,
        lastCalculated: currentDate
      },

      // Documents
      documents: [
        {
          type: 'accreditation',
          name: 'WAEC Accreditation Certificate',
          url: '/documents/prestige-high-accreditation.pdf',
          uploadDate: new Date('2024-01-15'),
          size: 2048576
        },
        {
          type: 'policy',
          name: 'School Code of Conduct',
          url: '/documents/code-of-conduct.pdf',
          uploadDate: new Date('2024-02-01'),
          size: 1024576
        }
      ],

      // Metadata
      metadata: {
        onboardingCompleted: true,
        profileCompleteness: 95,
        lastAdminLogin: new Date('2024-11-20'),
        dataUsage: {
          storageUsed: 356.8, // MB
          studentAccounts: 45,
          teacherAccounts: 12,
          assessmentsCreated: 67
        },
        compliance: {
          dataPrivacy: true,
          termsAccepted: true,
          gdprCompliant: true
        }
      },

      // Base Document Fields
      createdAt: new Date('2024-01-01'),
      updatedAt: currentDate,
      isDeleted: false
    },
    {
      // Basic Information
      _id: 'school_2',
      name: 'Excel Comprehensive College',
      code: 'ECC002',
      email: 'admin@excelcollege.edu.ng',
      phone: '+2348098765432',
      website: 'https://excelcollege.edu.ng',
      status: 'active',
      tier: 'standard',
      type: 'public',

      // Location
      address: {
        street: '456 Learning Boulevard',
        city: 'Surulere',
        state: 'Lagos',
        country: 'Nigeria',
        postalCode: '100012'
      },

      // Administrative
      principalName: 'Mrs. Chinedu Okoro',
      principalEmail: 'principal@excelcollege.edu.ng',
      principalPhone: '+2348087654321',
      foundedYear: 1985,
      motto: 'Striving for Excellence in Education',
      mission: 'To provide accessible quality education to all students',
      vision: 'To be a model public school in academic excellence',

      // Academic Structure
      classLevels: ['JSS1', 'JSS2', 'JSS3', 'SS1', 'SS2', 'SS3'],
      subjectsOffered: [
        'Mathematics', 'English Language', 'Physics', 'Chemistry', 'Biology',
        'Geography', 'History', 'Economics', 'Commerce', 'Government',
        'Literature in English', 'French', 'Fine Arts', 'Physical Education',
        'Computer Studies', 'Agricultural Science'
      ],
      academicSession: '2024/2025',
      currentTerm: 1,

      // Statistics
      statistics: {
        totalStudents: 28,
        totalTeachers: 8,
        totalStaff: 5,
        maleStudents: 15,
        femaleStudents: 13,
        classGroups: 12,
        lastUpdated: currentDate
      },

      // User References
      adminId: adminUsers.find(u => u.email === 'admin@excelcollege.edu.ng')?._id || 'admin_2',
      teacherIds: ['teacher_11', 'teacher_12', 'teacher_13', 'teacher_14', 'teacher_15'],
      studentIds: demoStudents.slice(45, 73).map(student => student._id),

      // Facilities
      facilities: [
        'Library', 'Science Lab', 'Computer Lab', 'Sports Field',
        'Art Room', 'Assembly Hall'
      ],

      // Features
      features: {
        careerGuidance: true,
        onlineAssessments: true,
        parentPortal: false,
        progressTracking: true,
        analytics: false
      },

      // Subscription
      subscription: {
        plan: 'standard',
        startDate: new Date('2024-02-01'),
        endDate: oneYearFromNow,
        isActive: true,
        billingEmail: 'admin@excelcollege.edu.ng'
      },

      // Branding
      branding: {
        logo: '/school-logos/excel-college.png',
        primaryColor: '#059669', // emerald-600
        secondaryColor: '#7C3AED', // violet-600
        favicon: '/favicons/excel-college.ico'
      },

      // Settings
      settings: {
        language: 'en',
        timezone: 'Africa/Lagos',
        dateFormat: 'DD/MM/YYYY',
        academicCalendar: {
          term1Start: new Date('2024-09-09'),
          term1End: new Date('2024-12-13'),
          term2Start: new Date('2025-01-06'),
          term3Start: new Date('2025-04-14'),
          term3End: new Date('2025-07-18')
        },
        assessmentSettings: {
          autoGrade: true,
          showResults: true,
          retakeAllowed: false
        }
      },

      // Notifications
      notifications: {
        email: {
          studentProgress: true,
          assessmentResults: true,
          announcements: true,
          billing: false
        },
        sms: {
          urgentAlerts: true,
          attendance: false
        },
        push: {
          general: true,
          deadlines: true
        }
      },

      // Integrations
      integrations: {
        lmsEnabled: false,
        paymentEnabled: false,
        smsEnabled: false
      },

      // Performance
      performance: {
        averageStudentPerformance: 72,
        studentEngagement: 78,
        assessmentCompletion: 85,
        teacherActivity: 82,
        lastCalculated: currentDate
      },

      // Documents
      documents: [
        {
          type: 'accreditation',
          name: 'State Accreditation Certificate',
          url: '/documents/excel-college-accreditation.pdf',
          uploadDate: new Date('2024-02-10'),
          size: 1572864
        }
      ],

      // Metadata
      metadata: {
        onboardingCompleted: true,
        profileCompleteness: 85,
        lastAdminLogin: new Date('2024-11-19'),
        dataUsage: {
          storageUsed: 128.5, // MB
          studentAccounts: 28,
          teacherAccounts: 8,
          assessmentsCreated: 34
        },
        compliance: {
          dataPrivacy: true,
          termsAccepted: true,
          gdprCompliant: false
        }
      },

      // Base Document Fields
      createdAt: new Date('2024-02-01'),
      updatedAt: currentDate,
      isDeleted: false
    },
    {
      // Basic Information - New School (Pending)
      _id: 'school_3',
      name: 'Brilliant Minds Academy',
      code: 'BMA003',
      email: 'admin@brilliantminds.edu.ng',
      phone: '+2348076543210',
      status: 'pending',
      tier: 'basic',
      type: 'private',

      // Location
      address: {
        street: '789 Innovation Road',
        city: 'Victoria Island',
        state: 'Lagos',
        country: 'Nigeria',
        postalCode: '101241'
      },

      // Administrative
      principalName: 'Mr. Femi Adekunle',
      principalEmail: 'principal@brilliantminds.edu.ng',
      principalPhone: '+2348065432109',
      foundedYear: 2024,
      motto: 'Nurturing Future Leaders',
      mission: 'To provide innovative education for the 21st century',
      vision: 'To be a center of educational excellence and innovation',

      // Academic Structure
      classLevels: ['JSS1', 'JSS2', 'JSS3'],
      subjectsOffered: [
        'Mathematics', 'English Language', 'Basic Science', 'Social Studies',
        'Computer Studies', 'Creative Arts', 'Physical Education'
      ],
      academicSession: '2024/2025',
      currentTerm: 1,

      // Statistics
      statistics: {
        totalStudents: 0,
        totalTeachers: 3,
        totalStaff: 2,
        maleStudents: 0,
        femaleStudents: 0,
        classGroups: 3,
        lastUpdated: currentDate
      },

      // User References
      adminId: adminUsers.find(u => u.email === 'admin@brilliantminds.edu.ng')?._id || 'admin_3',
      teacherIds: ['teacher_16', 'teacher_17', 'teacher_18'],
      studentIds: [],

      // Facilities
      facilities: [
        'Library', 'Computer Lab', 'Science Corner', 'Playground'
      ],

      // Features
      features: {
        careerGuidance: false,
        onlineAssessments: true,
        parentPortal: false,
        progressTracking: true,
        analytics: false
      },

      // Subscription
      subscription: {
        plan: 'basic',
        startDate: currentDate,
        endDate: oneYearFromNow,
        isActive: true,
        billingEmail: 'admin@brilliantminds.edu.ng'
      },

      // Branding
      branding: {
        primaryColor: '#7C3AED', // violet-600
        secondaryColor: '#F59E0B' // amber-500
      },

      // Settings
      settings: {
        language: 'en',
        timezone: 'Africa/Lagos',
        dateFormat: 'DD/MM/YYYY',
        academicCalendar: {
          term1Start: new Date('2024-09-09'),
          term1End: new Date('2024-12-13'),
          term2Start: new Date('2025-01-06'),
          term3Start: new Date('2025-04-14'),
          term3End: new Date('2025-07-18')
        },
        assessmentSettings: {
          autoGrade: true,
          showResults: true,
          retakeAllowed: true
        }
      },

      // Notifications
      notifications: {
        email: {
          studentProgress: true,
          assessmentResults: true,
          announcements: true,
          billing: true
        },
        sms: {
          urgentAlerts: false,
          attendance: false
        },
        push: {
          general: true,
          deadlines: true
        }
      },

      // Integrations
      integrations: {
        lmsEnabled: false,
        paymentEnabled: false,
        smsEnabled: false
      },

      // Performance
      performance: {
        averageStudentPerformance: 0,
        studentEngagement: 0,
        assessmentCompletion: 0,
        teacherActivity: 65,
        lastCalculated: currentDate
      },

      // Documents
      documents: [],

      // Metadata
      metadata: {
        onboardingCompleted: false,
        profileCompleteness: 60,
        lastAdminLogin: currentDate,
        dataUsage: {
          storageUsed: 5.2, // MB
          studentAccounts: 0,
          teacherAccounts: 3,
          assessmentsCreated: 0
        },
        compliance: {
          dataPrivacy: false,
          termsAccepted: true,
          gdprCompliant: false
        }
      },

      // Base Document Fields
      createdAt: currentDate,
      updatedAt: currentDate,
      isDeleted: false
    }
  ];
};

// Create school input data for registration
export const demoSchoolInputs: Partial<CreateSchoolInput>[] = [
  {
    name: 'Prestige High School',
    code: 'PHS001',
    email: 'admin@prestigehigh.edu.ng',
    phone: '+2348012345678',
    website: 'https://prestigehigh.edu.ng',
    status: 'active',
    tier: 'premium',
    type: 'private',
    address: {
      street: '123 Education Avenue',
      city: 'Ikeja',
      state: 'Lagos',
      country: 'Nigeria',
      postalCode: '100001'
    },
    principalName: 'Dr. Adebayo Johnson',
    principalEmail: 'principal@prestigehigh.edu.ng',
    principalPhone: '+2348023456789',
    foundedYear: 1995,
    motto: 'Knowledge, Character, Excellence'
  },
  {
    name: 'Excel Comprehensive College',
    code: 'ECC002',
    email: 'admin@excelcollege.edu.ng',
    phone: '+2348098765432',
    website: 'https://excelcollege.edu.ng',
    status: 'active',
    tier: 'standard',
    type: 'public',
    address: {
      street: '456 Learning Boulevard',
      city: 'Surulere',
      state: 'Lagos',
      country: 'Nigeria',
      postalCode: '100012'
    },
    principalName: 'Mrs. Chinedu Okoro',
    principalEmail: 'principal@excelcollege.edu.ng',
    principalPhone: '+2348087654321',
    foundedYear: 1985,
    motto: 'Striving for Excellence in Education'
  }
];

// School response data (without sensitive fields)
export const demoSchoolResponses: SchoolResponse[] = generateDemoSchools([], []).map(school => ({
  ...school,
  integrations: {
    ...school.integrations,
    apiKeys: undefined // Remove sensitive API keys
  }
}));

// Export for easy access
export const demoSchools = generateDemoSchools;